import {
  useWorkspaceConversionRate,
  useWorkspaceSettings,
} from '@easy-expense/data-firestore-client';
import { Expense, TaxRate } from '@easy-expense/data-schema-v2';
import { getTranslation, useIntlStore } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import {
  adoptTaxesAsOriginal,
  applyTaxEdit,
  calculateConversionRate,
  calculateEffectiveRate,
  convertedTaxes,
} from '@easy-expense/utils-shared';
import _ from 'lodash';
import React from 'react';

import { CurrencyField } from './CurrencyInput.component';
import { CurrencyTypeSelector } from './CurrencyType.component';
import {
  MeteredPaywallActions,
  useHandleMeteredAction,
} from '../../../hooks/useHandleMeteredAction';
import { LabelTextField } from '../../LabelTextField.component';

/** Width of the currency selector, matched by the rate block below it so the columns line up. */
const CURRENCY_COLUMN_WIDTH = 110;

export const ExpenseTotalField: React.FC<{
  loadedExpense: Expense | undefined;
  setExpense: React.Dispatch<React.SetStateAction<Partial<Expense>>>;
  expense: Partial<Expense>;
  renderKey: string;
  errors: { [k: string]: string[] } | undefined;
}> = ({ loadedExpense, setExpense, expense, renderKey, errors }) => {
  const { workspaceSettings } = useWorkspaceSettings();
  const [updatedLine1Total, setUpdatedLine1Total] = React.useState<number>();
  const [updatedLine2Total, setUpdatedLine2Total] = React.useState<number>();
  const [displayedConversionRate, setDisplayedConversionRate] = React.useState<number>();
  const [originalCurrencyIcon, setOriginalCurrencyIcon] = React.useState<string>('$');

  const [updatedTax, setUpdatedTax] = React.useState<number>();
  const [updatedSecondTax, setUpdatedSecondTax] = React.useState<number>();

  const conversionRates = useWorkspaceConversionRate(expense.date);
  const handleMeteredMultiCurrency = useHandleMeteredAction(MeteredPaywallActions.MULTI_CURRENCY);
  const [conversionRate, setConversionRate] = React.useState<number>();

  const hasChangedOriginal = React.useRef(false);
  const [isEditingForeignCurrency, setIsEditingForeignCurrency] = React.useState(false);

  const intlStore = useIntlStore();
  const workspaceCurrencySymbol = intlStore.cldr.Numbers.getCurrencySymbol(
    intlStore.currency,
    'narrow',
  );

  const [taxRate, secondTaxRate]: TaxRate[] | undefined = React.useMemo(() => {
    const trackedTaxRates = _.filter(workspaceSettings?.taxRates, (tr) => tr.tracked);
    return trackedTaxRates;
  }, [workspaceSettings]);

  // The Effective Rate is what a foreign amount is converted at. Undefined until there is a total
  // to derive it from. It is a starting point, not a constraint: the converted boxes can each be
  // overridden to match what the card actually charged, exactly as the converted total already can.
  const effectiveRate = calculateEffectiveRate(expense);
  // No rate, nothing to convert a foreign tax with, so there is nothing meaningful to type yet.
  const taxFieldsDisabled = isEditingForeignCurrency && effectiveRate === undefined;

  React.useEffect(() => {
    if (effectiveRate !== undefined) {
      setDisplayedConversionRate(effectiveRate);
    }
  }, [expense.originalTotal, expense.total]);

  React.useEffect(() => {
    if (expense?.originalCurrency) {
      setIsEditingForeignCurrency(true);
      setExpense((expense) =>
        adoptTaxesAsOriginal({
          ...expense,
          originalTotal: expense.originalTotal ? expense.originalTotal : expense.total,
        }),
      );
    } else {
      setIsEditingForeignCurrency(false);
      // The receipt taxes are dropped, not kept: a tax typed afterwards in the workspace currency
      // must not be overwritten by them if a foreign currency is picked again.
      setExpense((expense) => ({
        ...expense,
        total: expense.originalTotal ? expense.originalTotal : expense.total,
        tax: expense.originalTax ? expense.originalTax : expense.tax,
        secondTax: expense.originalSecondTax ? expense.originalSecondTax : expense.secondTax,
        originalTax: null,
        originalSecondTax: null,
      }));
    }
  }, [expense.originalCurrency]);

  React.useEffect(() => {
    if (loadedExpense) {
      if (loadedExpense.originalCurrency && loadedExpense.originalTotal) {
        setUpdatedLine1Total(loadedExpense.originalTotal ?? undefined);
        setUpdatedLine2Total(loadedExpense.total);

        // The receipt taxes were reconstructed where the expense was loaded, in ExpenseEdit.
        setUpdatedTax(loadedExpense.originalTax ?? undefined);
        setUpdatedSecondTax(loadedExpense.originalSecondTax ?? undefined);
      } else {
        setUpdatedLine1Total(loadedExpense.total);
        setUpdatedTax(loadedExpense.tax ?? undefined);
        setUpdatedSecondTax(loadedExpense.secondTax ?? undefined);
      }
    }
  }, [loadedExpense]);

  React.useEffect(() => {
    if (conversionRates) {
      if (expense.originalCurrency) {
        const rate = calculateConversionRate(expense, conversionRates);
        setConversionRate(rate);
      } else {
        setConversionRate(undefined);
      }
    }
  }, [conversionRates, expense.originalCurrency]);

  React.useEffect(() => {
    if (conversionRate && isEditingForeignCurrency) {
      const originalTotal = expense.originalTotal || 0;
      const total = originalTotal * conversionRate;
      if (hasChangedOriginal.current) {
        setExpense((expense) => ({
          ...expense,
          total,
          ...convertedTaxes(expense, conversionRate),
        }));
        setUpdatedLine2Total(total);
      }
    }
  }, [conversionRate, expense.originalTotal]);

  return (
    <Layout.Column>
      <Layout.Row>
        <Layout.Column style={{ width: CURRENCY_COLUMN_WIDTH }}>
          <CurrencyTypeSelector
            originalCurrency={expense.originalCurrency ?? null}
            onChange={(val) => {
              const applyCurrency = () => {
                hasChangedOriginal.current = true;
                setExpense((expense) => ({ ...expense, originalCurrency: val?.currency }));
                setOriginalCurrencyIcon(val?.icon ?? '$');
              };
              // Only a foreign currency is gated, as on mobile: the home currency needs no
              // conversion, so it stays free.
              if (val?.currency && val.currency !== intlStore.currency) {
                handleMeteredMultiCurrency(applyCurrency);
              } else {
                applyCurrency();
              }
            }}
          />
        </Layout.Column>
        <Layout.Column style={{ paddingLeft: '8px' }} grow>
          <CurrencyField
            key={`total_${renderKey}`}
            error={!!errors?.total}
            onChange={(total) => {
              if (isEditingForeignCurrency) {
                hasChangedOriginal.current = true;
                setExpense((expense) => ({ ...expense, originalTotal: total }));
              } else {
                setExpense((expense) => ({ ...expense, total }));
              }
            }}
            initialValue={expense.total}
            label={
              getTranslation('Total') +
              ((expense.total ?? 0) < 0 ? ' (' + getTranslation('refund') + ')' : '')
            }
            updatedValue={updatedLine1Total}
            showCurrency={false}
            allowNegative
          />
        </Layout.Column>

        {taxRate === undefined ? null : (
          <Layout.Column style={{ paddingLeft: '8px' }} grow>
            <CurrencyField
              key={`tax_${renderKey}`}
              error={!!errors?.tax}
              onChange={(tax) =>
                setExpense((expense) => applyTaxEdit(expense, { originalTax: tax }))
              }
              initialValue={expense?.tax ?? undefined}
              label={taxRate.name}
              showCurrency={false}
              updatedValue={updatedTax}
              disabled={taxFieldsDisabled}
            />
          </Layout.Column>
        )}
        {secondTaxRate === undefined ? null : (
          <Layout.Column style={{ paddingLeft: '8px' }} grow>
            <CurrencyField
              key={`secondTax_${renderKey}`}
              error={!!errors?.secondTax}
              onChange={(secondTax) =>
                setExpense((expense) => applyTaxEdit(expense, { originalSecondTax: secondTax }))
              }
              initialValue={expense?.secondTax ?? undefined}
              label={secondTaxRate.name}
              showCurrency={false}
              updatedValue={updatedSecondTax}
              disabled={taxFieldsDisabled}
            />
          </Layout.Column>
        )}
      </Layout.Row>
      {isEditingForeignCurrency ? (
        <Layout.Row>
          <Layout.Column style={{ width: CURRENCY_COLUMN_WIDTH }}>
            <Layout.Column align>
              <Icon name={'arrow-down'} size={10} />
            </Layout.Column>
            <LabelTextField label="" active={false}>
              <Layout.Row align>
                <OpenSans.Secondary size={16} weight="bold-700">
                  {workspaceCurrencySymbol}
                </OpenSans.Secondary>
                <Spacer.Horizontal size={8} />
                <OpenSans.Secondary size={16} weight="light-300">
                  {intlStore.currency}
                </OpenSans.Secondary>
              </Layout.Row>
            </LabelTextField>
            <Spacer.Vertical size={6} />
            <Layout.Row align>
              <OpenSans.Secondary size={16} weight="bold-700">
                {`${originalCurrencyIcon}1`}
              </OpenSans.Secondary>
              <Icon name={'arrow-forward'} size={10} />
              <OpenSans.Secondary size={16} weight="light-300">
                {`${workspaceCurrencySymbol}${displayedConversionRate?.toFixed(2) ?? conversionRate?.toFixed(2) ?? ''}`}
              </OpenSans.Secondary>
            </Layout.Row>
          </Layout.Column>

          <Layout.Column style={{ paddingLeft: '8px' }} grow>
            <Layout.Column align>
              <Icon name={'arrow-down'} size={10} />
            </Layout.Column>
            <CurrencyField
              onChange={(val) => {
                setExpense((expense) => ({
                  ...expense,
                  total: val,
                  ...convertedTaxes(expense, calculateEffectiveRate({ ...expense, total: val })),
                }));
              }}
              initialValue={expense.originalTotal ?? undefined}
              label={''}
              showCurrency={false}
              updatedValue={updatedLine2Total}
            />
          </Layout.Column>

          {taxRate === undefined ? null : (
            <Layout.Column style={{ paddingLeft: '8px' }} grow>
              <Layout.Column align>
                <Icon name={'arrow-down'} size={10} />
              </Layout.Column>
              <CurrencyField
                key={`convertedTax_${renderKey}`}
                onChange={(tax) => setExpense((expense) => ({ ...expense, tax }))}
                label={''}
                showCurrency={false}
                updatedValue={expense.tax ?? undefined}
              />
            </Layout.Column>
          )}
          {secondTaxRate === undefined ? null : (
            <Layout.Column style={{ paddingLeft: '8px' }} grow>
              <Layout.Column align>
                <Icon name={'arrow-down'} size={10} />
              </Layout.Column>
              <CurrencyField
                key={`convertedSecondTax_${renderKey}`}
                onChange={(secondTax) => setExpense((expense) => ({ ...expense, secondTax }))}
                label={''}
                showCurrency={false}
                updatedValue={expense.secondTax ?? undefined}
              />
            </Layout.Column>
          )}
        </Layout.Row>
      ) : null}
    </Layout.Column>
  );
};
